import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import { loadFromStorage, mockCrud } from '@/services/mockData'
import { TIER_FEATURES } from '@/services/stripe'
import type { UserFile, UserFileType, FileVersion, FileShareLink, ShareLinkExpiry } from '@/types'

const STORAGE_KEY = 'user_files'

const MOCK_USER_FILES: UserFile[] = [
  {
    id: 'file-1',
    user_id: 'demo-user-123',
    name: 'benchy.stl',
    original_name: 'benchy.stl',
    file_type: 'stl',
    size_bytes: 1_248_000,
    folder: '/',
    bucket_path: 'demo-user-123/benchy.stl',
    notes: 'Classic 3D printing benchmark',
    version_count: 1,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'file-2',
    user_id: 'demo-user-123',
    name: 'phone_stand.3mf',
    original_name: 'phone_stand.3mf',
    file_type: '3mf',
    size_bytes: 3_540_000,
    folder: '/projects',
    bucket_path: 'demo-user-123/projects/phone_stand.3mf',
    notes: 'Adjustable phone stand model',
    version_count: 1,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'file-3',
    user_id: 'demo-user-123',
    name: 'cable_clip.gcode',
    original_name: 'cable_clip.gcode',
    file_type: 'gcode',
    size_bytes: 820_000,
    folder: '/gcode',
    bucket_path: 'demo-user-123/gcode/cable_clip.gcode',
    notes: 'Sliced for Prusa MK3S+',
    version_count: 1,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
]

// Helper: generate a crypto-random hex token for share links
function generateToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32))
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

// Helper: convert expiry label to milliseconds
function expiryToMs(expiry: ShareLinkExpiry): number {
  switch (expiry) {
    case '1h':  return 3_600_000
    case '24h': return 86_400_000
    case '7d':  return 604_800_000
    case '30d': return 2_592_000_000
  }
}

function detectFileType(filename: string): UserFileType {
  const ext = filename.split('.').pop()?.toLowerCase() || ''
  // Handle multi-part extensions
  const fullName = filename.toLowerCase()
  // .gcode.3mf files are pre-sliced gcode archives (BambuStudio, Orca-FlashForge)
  if (fullName.endsWith('.gcode.3mf')) {
    return 'gcode'
  }
  // Slicer profile formats
  if (fullName.endsWith('.creality_filament') || fullName.endsWith('.orca_filament') || fullName.endsWith('.anycubic_filament') || fullName.endsWith('.elegoo_filament')) {
    return 'profile'
  }
  switch (ext) {
    case 'stl': return 'stl'
    case '3mf': return '3mf'
    case 'gcode': return 'gcode'
    case 'obj': return 'obj'
    case 'step':
    case 'stp': return 'step'
    // Slicer profile formats
    case 'bbsflmt': return 'profile'    // Bambu Studio
    default: return 'other'
  }
}

function getMimeForFileType(fileType: string): string {
  switch (fileType) {
    case 'stl': return 'model/stl'
    case '3mf': return 'model/3mf'
    case 'obj': return 'text/plain'
    case 'step': return 'model/step'
    case 'gcode': return 'text/plain'
    default: return 'application/octet-stream'
  }
}

const FOLDERS_STORAGE_KEY_PREFIX = 'user_folders_'

export const useFileStore = defineStore('file', () => {
  const files = ref<UserFile[]>([])
  const createdFolders = ref<string[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const currentFolder = ref<string>('/')
  const _loaded = ref(false)
  const _contextKey = ref<string>('personal')
  let foldersLoadedForUser: string | null = null

  function getFoldersStorageKey(userId: string) {
    return `${FOLDERS_STORAGE_KEY_PREFIX}${userId}`
  }

  function loadPersistedFolders(userId: string) {
    if (foldersLoadedForUser === userId) return // Already loaded for this user
    foldersLoadedForUser = userId

    const savedFolders = localStorage.getItem(getFoldersStorageKey(userId))
    if (savedFolders) {
      try { createdFolders.value = JSON.parse(savedFolders) } catch { createdFolders.value = [] }
    } else {
      createdFolders.value = []
    }

    // Migrate: if old unscoped key exists, move data to scoped key and remove it
    const oldKey = 'user_folders'
    const oldData = localStorage.getItem(oldKey)
    if (oldData && !savedFolders) {
      // Only migrate if there's no scoped data yet (first login after upgrade)
      localStorage.setItem(getFoldersStorageKey(userId), oldData)
      try { createdFolders.value = JSON.parse(oldData) } catch { createdFolders.value = [] }
    }
    // Always clean up the old key so it doesn't leak to other accounts
    if (oldData) {
      localStorage.removeItem(oldKey)
    }
  }

  const persistFolders = () => {
    if (!foldersLoadedForUser) return
    localStorage.setItem(getFoldersStorageKey(foldersLoadedForUser), JSON.stringify(createdFolders.value))
  }

  // Getters — merge file-derived folders with explicitly created ones
  const folders = computed(() => {
    const folderSet = new Set<string>()
    files.value.forEach(f => folderSet.add(f.folder))
    createdFolders.value.forEach(f => folderSet.add(f))
    return Array.from(folderSet).sort()
  })

  const filesByFolder = computed(() =>
    files.value.filter(f => f.folder === currentFolder.value)
  )

  const totalStorageUsed = computed(() =>
    files.value.reduce((sum, f) => sum + f.size_bytes, 0)
  )

  // Actions
  const fetchFiles = async (force = false, { silent = false, teamId }: { silent?: boolean; teamId?: string } = {}) => {
    const authStore = useAuthStore()
    if (!authStore.userId) {
      console.warn('[fileStore] fetchFiles called without userId')
      return
    }

    const newContext = teamId || 'personal'
    if (newContext !== _contextKey.value) {
      _loaded.value = false
      _contextKey.value = newContext
    }

    if (_loaded.value && !force) return

    // Load user-scoped folders from localStorage
    loadPersistedFolders(authStore.userId)

    if (!silent) {
      loading.value = true
      error.value = null
    }

    try {
      if (authStore.isDemoMode) {
        await new Promise(resolve => setTimeout(resolve, 200))
        files.value = loadFromStorage(STORAGE_KEY, MOCK_USER_FILES)
      } else {
        const { supabase } = await import('@/services/supabase')
        // Every column except the base64 `thumbnails`, which the Files grid
        // loads lazily for cards on screen (useFileThumbnails). `tags` comes
        // from migration 20260924000002; until that is applied the query
        // falls back to the columns without it.
        const base = 'id,user_id,team_id,name,original_name,file_type,size_bytes,folder,bucket_path,thumbnail_url,print_id,filament_id,notes,is_encrypted,sliced_for_printer,version_count,created_at,updated_at'
        // Paged: the API returns at most 1000 rows per request.
        const run = async (cols: string) => {
          const out: unknown[] = []
          for (let from = 0; ; from += 1000) {
            let query = supabase.from('user_files').select(cols)
            query = teamId ? query.eq('team_id', teamId) : query.is('team_id', null).eq('user_id', authStore.userId!)
            const { data, error: e } = await query.order('created_at', { ascending: false }).order('id').range(from, from + 999)
            if (e) return { data: null, error: e }
            out.push(...(data ?? []))
            if (!data || data.length < 1000) return { data: out, error: null }
          }
        }
        let { data, error: fetchError } = await run(base + ',tags')
        if (fetchError && /tags/.test(fetchError.message ?? '')) {
          ;({ data, error: fetchError } = await run(base))
        }
        if (fetchError) throw fetchError
        files.value = (data ?? []) as unknown as UserFile[]
      }
      _loaded.value = true
    } catch (err) {
      if (!silent) error.value = err instanceof Error ? err.message : 'Failed to fetch files'
    } finally {
      if (!silent) loading.value = false
    }
  }

  const clearData = () => {
    files.value = []
    createdFolders.value = []
    currentFolder.value = '/'
    _loaded.value = false
    foldersLoadedForUser = null
  }

  const uploadFile = async (file: File, folder: string, options?: { filamentId?: string; teamId?: string; slicedForPrinter?: string }): Promise<{ success: boolean; data?: any; error?: string }> => {
    // Make options mutable for auto-detection
    let opts = options || {}
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false, error: 'Not authenticated' }

    const teamId = opts.teamId

    // Check per-file size limit (skip for team context)
    const tier = authStore.subscriptionTier
    const tierFeatures = TIER_FEATURES[tier]
    const currentUsed = authStore.storageUsedBytes
    if (!teamId) {
      const maxFileSizeBytes = tierFeatures.maxFileSizeMb * 1024 * 1024
      if (file.size > maxFileSizeBytes) {
        return { success: false, error: `File exceeds the ${tierFeatures.maxFileSizeMb}MB limit for your ${tierFeatures.name} plan.` }
      }

      // Check storage quota
      const extraGb = authStore.storageExtraGb
      const maxBytes = (tierFeatures.storageGb + extraGb) * 1024 * 1024 * 1024

      if (currentUsed + file.size > maxBytes) {
        return { success: false, error: 'Storage quota exceeded. Upgrade your plan or purchase extra storage.' }
      }
    }

    loading.value = true
    error.value = null

    try {
      const fileType = detectFileType(file.name)
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const timestamp = Date.now()

      // Auto-detect printer model from gcode/3mf files if not explicitly provided
      if (!opts.slicedForPrinter && (fileType === 'gcode' || fileType === '3mf')) {
        try {
          const { extractMetadata } = await import('@/composables/useGcodeParser')
          const ext = file.name.toLowerCase()
          // Read 128KB head + tail — BambuStudio puts printer_model in CONFIG_BLOCK which can be past 32KB
          const CHUNK = 131072
          if (ext.endsWith('.gcode') && !ext.endsWith('.gcode.3mf')) {
            const headText = await file.slice(0, CHUNK).text()
            const tailStart = Math.max(0, file.size - CHUNK)
            const tailText = tailStart > CHUNK ? await file.slice(tailStart).text() : ''
            const meta = extractMetadata(headText + '\n' + tailText)
            if (meta.printerModel) opts.slicedForPrinter = meta.printerModel
          } else if (ext.endsWith('.gcode.3mf') || ext.endsWith('.3mf')) {
            const JSZip = (await import('jszip')).default
            const buf = await file.arrayBuffer()
            const zip = await JSZip.loadAsync(buf)
            // Try gcode inside the 3MF first (has full printer name like "Bambu Lab A1 Mini")
            const gcodeEntry = zip.file(/plate_\d+\.gcode$/i)?.[0] || zip.file(/\.gcode$/i)?.[0]
            if (gcodeEntry) {
              const gcodeText = await gcodeEntry.async('string')
              const head = gcodeText.slice(0, CHUNK)
              // Log lines containing "printer" or "model" or "machine" for debugging
              const debugLines = head.split('\n').filter(l => /printer|model|machine/i.test(l)).slice(0, 10)
              const tail = gcodeText.length > CHUNK * 2 ? gcodeText.slice(-CHUNK) : ''
              const meta = extractMetadata(head + '\n' + tail)
              if (meta.printerModel) opts.slicedForPrinter = meta.printerModel
            }
            // Fallback: slice_info.config for Bambu 3MF (has model ID code like "N2S")
            if (!opts.slicedForPrinter) {
              const sliceInfo = zip.file('Metadata/slice_info.config')
              if (sliceInfo) {
                const xml = await sliceInfo.async('string')
                const modelMatch = xml.match(/printer_model_id["\s]+value="([^"]+)"/)
                if (modelMatch) opts.slicedForPrinter = modelMatch[1].trim()
              }
            }
          }
        } catch { /* metadata extraction is optional */ }
      }

      if (authStore.isDemoMode) {
        const newFile = mockCrud.create(STORAGE_KEY, {
          user_id: authStore.userId,
          name: file.name,
          original_name: file.name,
          file_type: fileType,
          size_bytes: file.size,
          folder,
          bucket_path: `${authStore.userId}${folder === '/' ? '/' : folder + '/'}${timestamp}-${safeName}`,
          ...(opts.slicedForPrinter ? { sliced_for_printer: opts.slicedForPrinter } : {}),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        } as UserFile, files.value)
        files.value.unshift(newFile)

        // Update storage used in demo profile
        await authStore.updateProfile({
          storage_used_bytes: currentUsed + file.size
        })

        return { success: true, data: newFile }
      } else {
        const { supabase } = await import('@/services/supabase')
        const storagePath = `${authStore.userId}${folder === '/' ? '/' : folder + '/'}${timestamp}-${safeName}`

        // Ensure user has an encryption key (creates one on first upload)
        await ensureEncryptionKey()

        // Encrypt the file before upload
        const { isEncryptionReady, encryptFile } = await import('@/composables/useFileEncryption')
        let uploadData: File | Blob = file
        let isEncrypted = false

        if (isEncryptionReady()) {
          const plainBuffer = await file.arrayBuffer()
          const encryptedBuffer = await encryptFile(plainBuffer)
          uploadData = new Blob([encryptedBuffer], { type: 'application/octet-stream' })
          isEncrypted = true
        }

        // Upload to Supabase storage bucket
        const { error: uploadError } = await supabase.storage
          .from('user-files')
          .upload(storagePath, uploadData)

        if (uploadError) {
          console.error('[FileStore] Upload error:', uploadError.message, uploadError)
          throw uploadError
        }

        // Insert metadata into user_files table
        const insertData: Record<string, unknown> = {
            user_id: authStore.userId,
            name: file.name,
            original_name: file.name,
            file_type: fileType,
            size_bytes: file.size, // Store original size for display/quota
            folder,
            bucket_path: storagePath,
            is_encrypted: isEncrypted,
            ...(teamId ? { team_id: teamId } : {}),
        }
        if (opts.filamentId) insertData.filament_id = opts.filamentId
        if (opts.slicedForPrinter) insertData.sliced_for_printer = opts.slicedForPrinter

        const { data, error: insertError } = await supabase
          .from('user_files')
          .insert(insertData)
          .select()
          .single()

        if (insertError) throw insertError

        // Increment storage_used_bytes on profile
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ storage_used_bytes: currentUsed + file.size })
          .eq('user_id', authStore.userId)

        if (profileError) throw profileError

        await authStore.fetchProfile()
        files.value.unshift(data as UserFile)
        return { success: true, data }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to upload file'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const deleteFile = async (id: string) => {
    const authStore = useAuthStore()
    loading.value = true

    try {
      const file = files.value.find(f => f.id === id)
      if (!file) throw new Error('File not found')

      if (authStore.isDemoMode) {
        mockCrud.delete(STORAGE_KEY, id, files.value)
        files.value = files.value.filter(f => f.id !== id)

        // Decrement storage used in demo profile
        const currentUsed = authStore.storageUsedBytes
        await authStore.updateProfile({
          storage_used_bytes: Math.max(0, currentUsed - file.size_bytes)
        })

        return { success: true }
      } else {
        const { supabase } = await import('@/services/supabase')

        // 1. Collect version bucket paths and sizes for cleanup
        const { data: versions } = await supabase
          .from('file_versions')
          .select('bucket_path, size_bytes')
          .eq('file_id', id)

        const versionPaths = (versions || []).map((v: { bucket_path: string }) => v.bucket_path)
        const versionBytes = (versions || []).reduce((sum: number, v: { size_bytes: number }) => sum + v.size_bytes, 0)

        // 2. Collect share link bucket paths for cleanup
        const { data: shareLinks } = await supabase
          .from('file_share_links')
          .select('share_bucket_path')
          .eq('file_id', id)

        const sharePaths = (shareLinks || []).map((l: { share_bucket_path: string }) => l.share_bucket_path)

        // 3. Delete version files from user-files storage
        const allUserFilePaths = [file.bucket_path, ...versionPaths]
        const { error: storageError } = await supabase.storage
          .from('user-files')
          .remove(allUserFilePaths)

        if (storageError) throw storageError

        // 4. Delete share copies from shared-files storage
        if (sharePaths.length > 0) {
          const { error: shareStorageError } = await supabase.storage
            .from('shared-files')
            .remove(sharePaths)

          if (shareStorageError) console.warn('[FileStore] Failed to clean up shared copies:', shareStorageError.message)
        }

        // 5. Delete metadata row (cascades to file_versions and file_share_links)
        const { error: deleteError } = await supabase
          .from('user_files')
          .delete()
          .eq('id', id)

        if (deleteError) throw deleteError

        // 6. Decrement storage_used_bytes (current file + all versions)
        const totalFreed = file.size_bytes + versionBytes
        const currentUsed = authStore.storageUsedBytes
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ storage_used_bytes: Math.max(0, currentUsed - totalFreed) })
          .eq('user_id', authStore.userId)

        if (profileError) throw profileError

        await authStore.fetchProfile()
        files.value = files.value.filter(f => f.id !== id)
        return { success: true }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete file'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const createFolder = (path: string) => {
    // Folders are virtual — track explicitly created folders so they display even when empty
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    if (!createdFolders.value.includes(normalizedPath)) {
      createdFolders.value.push(normalizedPath)
      persistFolders()
    }
  }

  // Get all files in a folder and its subfolders
  const getFilesInFolder = (path: string): UserFile[] => {
    return files.value.filter(f => f.folder === path || f.folder.startsWith(path + '/'))
  }

  const deleteFolder = async (path: string, force = false) => {
    const authStore = useAuthStore()
    const folderFiles = getFilesInFolder(path)


    // If folder has files and force is not set, return info about contents
    if (folderFiles.length > 0 && !force) {
      const totalSize = folderFiles.reduce((sum, f) => sum + f.size_bytes, 0)
      return { success: false, error: 'Folder is not empty', fileCount: folderFiles.length, totalSize }
    }

    loading.value = true
    try {
      // Delete all files in the folder (and subfolders)
      if (folderFiles.length > 0) {
        if (authStore.isDemoMode) {
          let freedBytes = 0
          for (const file of folderFiles) {
            mockCrud.delete(STORAGE_KEY, file.id, files.value)
            freedBytes += file.size_bytes
          }
          files.value = files.value.filter(f => f.folder !== path && !f.folder.startsWith(path + '/'))
          const currentUsed = authStore.storageUsedBytes
          await authStore.updateProfile({
            storage_used_bytes: Math.max(0, currentUsed - freedBytes)
          })
        } else {
          const { supabase } = await import('@/services/supabase')

          // Delete from storage bucket (batch)
          const bucketPaths = folderFiles.map(f => f.bucket_path)
          const { error: storageError } = await supabase.storage
            .from('user-files')
            .remove(bucketPaths)

          if (storageError) throw storageError

          // Delete metadata rows
          const fileIds = folderFiles.map(f => f.id)
          const { error: deleteError } = await supabase
            .from('user_files')
            .delete()
            .in('id', fileIds)

          if (deleteError) throw deleteError

          // Update storage_used_bytes
          const freedBytes = folderFiles.reduce((sum, f) => sum + f.size_bytes, 0)
          const currentUsed = authStore.storageUsedBytes
          const { error: profileError } = await supabase
            .from('profiles')
            .update({ storage_used_bytes: Math.max(0, currentUsed - freedBytes) })
            .eq('user_id', authStore.userId)

          if (profileError) throw profileError

          await authStore.fetchProfile()
          files.value = files.value.filter(f => !fileIds.includes(f.id))
        }
      }

      // Remove from createdFolders (exact match and any child folders)
      createdFolders.value = createdFolders.value.filter(f => f !== path && !f.startsWith(path + '/'))
      persistFolders()

      return { success: true, deletedFiles: folderFiles.length }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete folder'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const renameFile = async (id: string, newName: string) => {
    const authStore = useAuthStore()
    loading.value = true

    try {
      if (authStore.isDemoMode) {
        const updated = mockCrud.update(STORAGE_KEY, id, { name: newName }, files.value)
        if (updated) {
          const index = files.value.findIndex(f => f.id === id)
          if (index !== -1) files.value[index] = updated
        }
        return { success: true, data: updated }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: updateError } = await supabase
          .from('user_files')
          .update({ name: newName, updated_at: new Date().toISOString() })
          .eq('id', id)
          .select()
          .single()

        if (updateError) throw updateError
        const index = files.value.findIndex(f => f.id === id)
        if (index !== -1) files.value[index] = data as UserFile
        return { success: true, data }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to rename file'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  /** Replace a file's tags (trimmed, de-duplicated case-insensitively, max 50 × 40 chars). */
  const updateTags = async (id: string, tags: string[]) => {
    const authStore = useAuthStore()
    const seen = new Set<string>()
    const clean = tags
      .map(t => t.trim().slice(0, 40))
      .filter(t => t && !seen.has(t.toLowerCase()) && seen.add(t.toLowerCase()))
      .slice(0, 50)
    try {
      if (authStore.isDemoMode) {
        const updated = mockCrud.update(STORAGE_KEY, id, { tags: clean }, files.value)
        const index = files.value.findIndex(f => f.id === id)
        if (index !== -1 && updated) files.value[index] = updated
        return { success: true }
      }
      const { supabase } = await import('@/services/supabase')
      // The generated DB types predate `user_files`; query it untyped.
      const { error: updateError } = await (supabase as any).from('user_files').update({ tags: clean }).eq('id', id)
      if (updateError) throw updateError
      const index = files.value.findIndex(f => f.id === id)
      if (index !== -1) files.value[index] = { ...files.value[index], tags: clean }
      return { success: true }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to update tags' }
    }
  }

  /**
   * Move files to another folder. Folders are virtual (the `folder` column),
   * so the stored bytes stay where they are.
   */
  const moveFiles = async (ids: string[], folder: string) => {
    const authStore = useAuthStore()
    const target = folder === '/' ? '/' : '/' + folder.split('/').filter(Boolean).join('/')
    try {
      if (authStore.isDemoMode) {
        for (const id of ids) {
          const updated = mockCrud.update(STORAGE_KEY, id, { folder: target }, files.value)
          const index = files.value.findIndex(f => f.id === id)
          if (index !== -1 && updated) files.value[index] = updated
        }
        return { success: true }
      }
      const { supabase } = await import('@/services/supabase')
      const { error: updateError } = await (supabase as any)
        .from('user_files')
        .update({ folder: target, updated_at: new Date().toISOString() })
        .in('id', ids)
      if (updateError) throw updateError
      files.value = files.value.map(f => (ids.includes(f.id) ? { ...f, folder: target } : f))
      return { success: true }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to move files' }
    }
  }

  /** Copy a file (bytes and details) as "<name> copy.<ext>" in the same folder. */
  const duplicateFile = async (id: string) => {
    const authStore = useAuthStore()
    const src = files.value.find(f => f.id === id)
    if (!src) return { success: false, error: 'File not found' }
    const dot = src.name.lastIndexOf('.')
    const copyName = dot > 0 ? `${src.name.slice(0, dot)} copy${src.name.slice(dot)}` : `${src.name} copy`
    try {
      if (authStore.isDemoMode) {
        const created = mockCrud.create(STORAGE_KEY, { ...src, id: undefined, name: copyName, version_count: 1 } as any, files.value)
        if (created) files.value = [created as UserFile, ...files.value]
        return { success: true }
      }
      const { supabase } = await import('@/services/supabase')
      const slash = src.bucket_path.lastIndexOf('/')
      const newPath = `${src.bucket_path.slice(0, slash + 1)}${Date.now()}-${copyName.replace(/[^a-zA-Z0-9._-]/g, '_')}`
      const { error: copyError } = await supabase.storage.from('user-files').copy(src.bucket_path, newPath)
      if (copyError) throw copyError
      const row = {
        user_id: src.user_id,
        team_id: src.team_id ?? null,
        name: copyName,
        original_name: src.original_name,
        file_type: src.file_type,
        size_bytes: src.size_bytes,
        folder: src.folder,
        bucket_path: newPath,
        notes: src.notes ?? null,
        is_encrypted: src.is_encrypted ?? false,
        sliced_for_printer: src.sliced_for_printer ?? null,
        ...(src.tags?.length ? { tags: src.tags } : {}),
      }
      const { data, error: insertError } = await (supabase as any).from('user_files').insert(row).select('*').single()
      if (insertError) {
        await supabase.storage.from('user-files').remove([newPath])
        throw insertError
      }
      files.value = [data as UserFile, ...files.value]
      await authStore.fetchProfile?.()
      return { success: true }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to duplicate file' }
    }
  }

  const updateNotes = async (id: string, notes: string) => {
    const authStore = useAuthStore()
    loading.value = true

    try {
      if (authStore.isDemoMode) {
        const updated = mockCrud.update(STORAGE_KEY, id, { notes }, files.value)
        if (updated) {
          const index = files.value.findIndex(f => f.id === id)
          if (index !== -1) files.value[index] = updated
        }
        return { success: true, data: updated }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: updateError } = await supabase
          .from('user_files')
          .update({ notes, updated_at: new Date().toISOString() })
          .eq('id', id)
          .select()
          .single()

        if (updateError) throw updateError
        const index = files.value.findIndex(f => f.id === id)
        if (index !== -1) files.value[index] = data as UserFile
        return { success: true, data }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update notes'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const getDownloadUrl = async (file: UserFile) => {
    const authStore = useAuthStore()

    if (authStore.isDemoMode) {
      return file.bucket_path
    }

    const { supabase } = await import('@/services/supabase')
    const { data, error: urlError } = await supabase.storage
      .from('user-files')
      .createSignedUrl(file.bucket_path, 3600) // 1 hour expiry

    if (urlError) throw urlError
    return data.signedUrl
  }

  /**
   * Ensure the current user has an encryption key.
   * Creates one on the first encrypted upload (lazy initialization).
   */
  const ensureEncryptionKey = async () => {
    const authStore = useAuthStore()
    if (authStore.isDemoMode) return

    const { isEncryptionConfigured, isEncryptionReady, generateUserKey, loadUserKey } = await import('@/composables/useFileEncryption')
    if (!isEncryptionConfigured()) return
    if (isEncryptionReady()) return // Already loaded

    // Check if profile already has a key (may not be loaded yet)
    if (authStore.profile?.encryption_key_wrapped) {
      await loadUserKey(authStore.profile.encryption_key_wrapped)
      return
    }

    // Generate a new key for this user
    const wrappedKey = await generateUserKey()

    const { supabase } = await import('@/services/supabase')
    await supabase
      .from('profiles')
      .update({ encryption_key_wrapped: wrappedKey })
      .eq('user_id', authStore.userId)

    // Re-fetch profile so the key is available for future logins
    await authStore.fetchProfile()
  }

  /**
   * Fetch file content, decrypting if necessary.
   *
   * For encrypted files: fetches the raw bytes, decrypts, and returns a blob: URL.
   *   The caller is responsible for revoking via URL.revokeObjectURL() when done.
   * For unencrypted files: returns a signed HTTPS URL directly (no fetch needed).
   */
  const getFileContent = async (file: UserFile): Promise<string> => {
    const authStore = useAuthStore()

    if (authStore.isDemoMode) {
      return file.bucket_path
    }

    const { supabase } = await import('@/services/supabase')

    // Get a signed URL
    const { data: urlData, error: urlError } = await supabase.storage
      .from('user-files')
      .createSignedUrl(file.bucket_path, 3600)

    if (urlError) throw urlError

    // Unencrypted files — return the signed URL directly (Three.js loaders, window.open, etc. can use it as-is)
    if (!file.is_encrypted) {
      return urlData.signedUrl
    }

    // Encrypted files — fetch, decrypt, return blob URL
    const response = await fetch(urlData.signedUrl)
    if (!response.ok) throw new Error(`Failed to fetch file: ${response.status}`)

    let buffer = await response.arrayBuffer()

    const { decryptFile } = await import('@/composables/useFileEncryption')
    buffer = await decryptFile(buffer)

    const mimeType = getMimeForFileType(file.file_type)
    const blob = new Blob([buffer], { type: mimeType })
    return URL.createObjectURL(blob)
  }

  // ==========================================
  // VERSIONING ACTIONS
  // ==========================================

  /**
   * Upload a new version of an existing file.
   * Saves the current file state as a version, then replaces the file content.
   */
  const uploadNewVersion = async (fileId: string, newFile: File) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false, error: 'Not authenticated' }

    const existingFile = files.value.find(f => f.id === fileId)
    if (!existingFile) return { success: false, error: 'File not found' }

    // Check per-file size limit
    const tier = authStore.subscriptionTier
    const tierFeatures = TIER_FEATURES[tier]
    const maxFileSizeBytes = tierFeatures.maxFileSizeMb * 1024 * 1024
    if (newFile.size > maxFileSizeBytes) {
      return { success: false, error: `File exceeds the ${tierFeatures.maxFileSizeMb}MB limit for your ${tierFeatures.name} plan.` }
    }

    // Check storage quota (new version adds to total; old version stays)
    const extraGb = authStore.storageExtraGb
    const maxBytes = (tierFeatures.storageGb + extraGb) * 1024 * 1024 * 1024
    const currentUsed = authStore.storageUsedBytes
    if (currentUsed + newFile.size > maxBytes) {
      return { success: false, error: 'Storage quota exceeded. Upgrade your plan or purchase extra storage.' }
    }

    loading.value = true
    error.value = null

    try {
      if (authStore.isDemoMode) {
        // Save current state as a version
        const versionNumber = existingFile.version_count || 1
        // In demo mode, just update version_count on the file
        const updatedFile = mockCrud.update(STORAGE_KEY, fileId, {
          name: newFile.name,
          original_name: newFile.name,
          file_type: detectFileType(newFile.name),
          size_bytes: newFile.size,
          version_count: versionNumber + 1,
          updated_at: new Date().toISOString()
        }, files.value)

        if (updatedFile) {
          const index = files.value.findIndex(f => f.id === fileId)
          if (index !== -1) files.value[index] = updatedFile
        }

        // Update storage used
        await authStore.updateProfile({
          storage_used_bytes: currentUsed + newFile.size
        })

        return { success: true, data: updatedFile }
      } else {
        const { supabase } = await import('@/services/supabase')

        // 1. Save current file state as a version in file_versions
        const versionNumber = existingFile.version_count || 1
        const { error: versionError } = await supabase
          .from('file_versions')
          .insert({
            file_id: fileId,
            version_number: versionNumber,
            bucket_path: existingFile.bucket_path,
            size_bytes: existingFile.size_bytes,
            original_name: existingFile.original_name,
            is_encrypted: existingFile.is_encrypted || false
          })

        if (versionError) throw versionError

        // 2. Encrypt and upload the new file
        await ensureEncryptionKey()
        const { isEncryptionReady, encryptFile } = await import('@/composables/useFileEncryption')
        let uploadData: File | Blob = newFile
        let isEncrypted = false

        if (isEncryptionReady()) {
          const plainBuffer = await newFile.arrayBuffer()
          const encryptedBuffer = await encryptFile(plainBuffer)
          uploadData = new Blob([encryptedBuffer], { type: 'application/octet-stream' })
          isEncrypted = true
        }

        const safeName = newFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')
        const timestamp = Date.now()
        const folder = existingFile.folder
        const newPath = `${authStore.userId}${folder === '/' ? '/' : folder + '/'}${timestamp}-${safeName}`

        const { error: uploadError } = await supabase.storage
          .from('user-files')
          .upload(newPath, uploadData)

        if (uploadError) throw uploadError

        // 3. Update user_files row with new file info
        const newVersionCount = versionNumber + 1
        const { data, error: updateError } = await supabase
          .from('user_files')
          .update({
            bucket_path: newPath,
            name: newFile.name,
            original_name: newFile.name,
            file_type: detectFileType(newFile.name),
            size_bytes: newFile.size,
            is_encrypted: isEncrypted,
            version_count: newVersionCount,
            updated_at: new Date().toISOString()
          })
          .eq('id', fileId)
          .select()
          .single()

        if (updateError) throw updateError

        // 4. Update storage quota (new version adds to total)
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ storage_used_bytes: currentUsed + newFile.size })
          .eq('user_id', authStore.userId)

        if (profileError) throw profileError

        await authStore.fetchProfile()
        const index = files.value.findIndex(f => f.id === fileId)
        if (index !== -1) files.value[index] = data as UserFile
        return { success: true, data }
      }
    } catch (err) {
      console.error('[FileStore] uploadNewVersion error:', err)
      error.value = err instanceof Error ? err.message : 'Failed to upload new version'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch version history for a file.
   */
  const getVersions = async (fileId: string): Promise<FileVersion[]> => {
    const authStore = useAuthStore()

    if (authStore.isDemoMode) {
      // Demo mode has no real versions
      return []
    }

    const { supabase } = await import('@/services/supabase')
    const { data, error: fetchError } = await supabase
      .from('file_versions')
      .select('*')
      .eq('file_id', fileId)
      .order('version_number', { ascending: false })

    if (fetchError) throw fetchError
    return (data || []) as FileVersion[]
  }

  /**
   * Download a specific version of a file.
   * Handles decryption for encrypted versions.
   */
  const downloadVersion = async (version: FileVersion) => {
    const authStore = useAuthStore()

    if (authStore.isDemoMode) {
      return // No real files in demo
    }

    const { supabase } = await import('@/services/supabase')
    const { data: urlData, error: urlError } = await supabase.storage
      .from('user-files')
      .createSignedUrl(version.bucket_path, 3600)

    if (urlError) throw urlError

    if (!version.is_encrypted) {
      // Direct download via signed URL
      const a = document.createElement('a')
      a.href = urlData.signedUrl
      a.download = version.original_name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      return
    }

    // Encrypted: fetch, decrypt, trigger download
    const response = await fetch(urlData.signedUrl)
    if (!response.ok) throw new Error(`Failed to fetch version: ${response.status}`)

    let buffer = await response.arrayBuffer()
    const { decryptFile } = await import('@/composables/useFileEncryption')
    buffer = await decryptFile(buffer)

    const mimeType = getMimeForFileType(version.original_name.split('.').pop()?.toLowerCase() || 'other')
    const blob = new Blob([buffer], { type: mimeType })
    const blobUrl = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = blobUrl
    a.download = version.original_name
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(blobUrl)
  }

  /**
   * Delete a specific version of a file.
   * Removes the version's storage object and database row, updates version_count and storage quota.
   */
  const deleteVersion = async (version: FileVersion) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false, error: 'Not authenticated' }

    loading.value = true
    error.value = null

    try {
      if (authStore.isDemoMode) {
        return { success: true }
      }

      const { supabase } = await import('@/services/supabase')

      // 1. Delete version file from storage
      const { error: storageError } = await supabase.storage
        .from('user-files')
        .remove([version.bucket_path])

      if (storageError) console.warn('[FileStore] Failed to delete version file from storage:', storageError.message)

      // 2. Delete version record from database
      const { error: deleteError } = await supabase
        .from('file_versions')
        .delete()
        .eq('id', version.id)

      if (deleteError) throw deleteError

      // 3. Decrement version_count on the parent file
      const file = files.value.find(f => f.id === version.file_id)
      if (file) {
        const newCount = Math.max(1, (file.version_count || 1) - 1)
        const { data: updatedFile, error: updateError } = await supabase
          .from('user_files')
          .update({ version_count: newCount, updated_at: new Date().toISOString() })
          .eq('id', version.file_id)
          .select()
          .single()

        if (updateError) console.warn('[FileStore] Failed to update version_count:', updateError.message)

        if (updatedFile) {
          const index = files.value.findIndex(f => f.id === version.file_id)
          if (index !== -1) files.value[index] = updatedFile as UserFile
        }
      }

      // 4. Decrement storage_used_bytes
      const currentUsed = authStore.storageUsedBytes
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ storage_used_bytes: Math.max(0, currentUsed - version.size_bytes) })
        .eq('user_id', authStore.userId)

      if (profileError) console.warn('[FileStore] Failed to update storage quota:', profileError.message)

      await authStore.fetchProfile()
      return { success: true }
    } catch (err) {
      console.error('[FileStore] deleteVersion error:', err)
      error.value = err instanceof Error ? err.message : 'Failed to delete version'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * Restore a previous version as the current file.
   * Swaps: current file state → saved as a new version, selected version → becomes current.
   * The version record for the restored version is removed (it's now current).
   * No storage files are moved — only the metadata pointers swap.
   */
  const restoreVersion = async (version: FileVersion) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false, error: 'Not authenticated' }

    const file = files.value.find(f => f.id === version.file_id)
    if (!file) return { success: false, error: 'File not found' }

    loading.value = true
    error.value = null

    try {
      if (authStore.isDemoMode) {
        return { success: true }
      }

      const { supabase } = await import('@/services/supabase')

      // 1. Save current file state as a new version (takes the next version number)
      const currentVersionNumber = file.version_count || 1
      const newVersionNumber = currentVersionNumber + 1
      const { error: saveCurrentError } = await supabase
        .from('file_versions')
        .insert({
          file_id: file.id,
          version_number: currentVersionNumber,
          bucket_path: file.bucket_path,
          size_bytes: file.size_bytes,
          original_name: file.original_name,
          is_encrypted: file.is_encrypted || false
        })

      if (saveCurrentError) throw saveCurrentError

      // 2. Update user_files row to point to the restored version's data
      const fileType = detectFileType(version.original_name)
      const { data: updatedFile, error: updateError } = await supabase
        .from('user_files')
        .update({
          bucket_path: version.bucket_path,
          name: version.original_name,
          original_name: version.original_name,
          file_type: fileType,
          size_bytes: version.size_bytes,
          is_encrypted: version.is_encrypted,
          version_count: newVersionNumber,
          updated_at: new Date().toISOString()
        })
        .eq('id', file.id)
        .select()
        .single()

      if (updateError) throw updateError

      // 3. Delete the restored version's record (it's now the current file)
      const { error: deleteVersionError } = await supabase
        .from('file_versions')
        .delete()
        .eq('id', version.id)

      if (deleteVersionError) console.warn('[FileStore] Failed to remove restored version record:', deleteVersionError.message)

      // 4. Update local state
      if (updatedFile) {
        const index = files.value.findIndex(f => f.id === file.id)
        if (index !== -1) files.value[index] = updatedFile as UserFile
      }

      return { success: true, data: updatedFile }
    } catch (err) {
      console.error('[FileStore] restoreVersion error:', err)
      error.value = err instanceof Error ? err.message : 'Failed to restore version'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  // ==========================================
  // SHARING ACTIONS
  // ==========================================

  /**
   * Create a one-time share link for a file (or a specific version).
   * Decrypts the file and uploads an unencrypted copy to the shared-files bucket.
   */
  const createShareLink = async (fileId: string, expiry: ShareLinkExpiry, versionId?: string, maxDownloads: number = 1) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false, error: 'Not authenticated' }

    const file = files.value.find(f => f.id === fileId)
    if (!file) return { success: false, error: 'File not found' }

    loading.value = true
    error.value = null

    try {
      if (authStore.isDemoMode) {
        // Generate a fake share link for demo
        const token = generateToken()
        const shareLink: FileShareLink = {
          id: `share-${Date.now()}`,
          file_id: fileId,
          version_id: versionId || null,
          token,
          share_bucket_path: `demo/${token}-${file.name}`,
          file_name: file.name,
          file_size_bytes: file.size_bytes,
          expires_at: new Date(Date.now() + expiryToMs(expiry)).toISOString(),
          downloaded_at: null,
          max_downloads: maxDownloads,
          download_count: 0,
          created_by: authStore.userId,
          created_at: new Date().toISOString()
        }
        const url = `${window.location.origin}/share/${token}`
        return { success: true, data: shareLink, url }
      } else {
        const { supabase } = await import('@/services/supabase')

        // Determine source: specific version or current file
        let sourceBucketPath = file.bucket_path
        let sourceIsEncrypted = file.is_encrypted || false
        let sourceFileName = file.original_name
        let sourceSize = file.size_bytes

        if (versionId) {
          const { data: versionData, error: versionError } = await supabase
            .from('file_versions')
            .select('*')
            .eq('id', versionId)
            .single()

          if (versionError) throw versionError
          const version = versionData as FileVersion
          sourceBucketPath = version.bucket_path
          sourceIsEncrypted = version.is_encrypted
          sourceFileName = version.original_name
          sourceSize = version.size_bytes
        }

        // Fetch the file content (handles decryption automatically)
        const { data: urlData, error: urlError } = await supabase.storage
          .from('user-files')
          .createSignedUrl(sourceBucketPath, 3600)

        if (urlError) throw urlError

        let fileBlob: Blob

        if (sourceIsEncrypted) {
          // Decrypt the file
          const response = await fetch(urlData.signedUrl)
          if (!response.ok) throw new Error(`Failed to fetch file: ${response.status}`)
          let buffer = await response.arrayBuffer()

          const { decryptFile } = await import('@/composables/useFileEncryption')
          buffer = await decryptFile(buffer)
          fileBlob = new Blob([buffer])
        } else {
          // Fetch unencrypted file as-is
          const response = await fetch(urlData.signedUrl)
          if (!response.ok) throw new Error(`Failed to fetch file: ${response.status}`)
          fileBlob = await response.blob()
        }

        // Upload unencrypted copy to shared-files bucket
        const token = generateToken()
        const sharePath = `${authStore.userId}/share/${token}-${sourceFileName}`

        const { error: shareUploadError } = await supabase.storage
          .from('shared-files')
          .upload(sharePath, fileBlob)

        if (shareUploadError) throw shareUploadError

        // Create share link record
        const expiresAt = new Date(Date.now() + expiryToMs(expiry)).toISOString()
        const { data: linkData, error: linkError } = await supabase
          .from('file_share_links')
          .insert({
            file_id: fileId,
            version_id: versionId || null,
            token,
            share_bucket_path: sharePath,
            file_name: sourceFileName,
            file_size_bytes: sourceSize,
            expires_at: expiresAt,
            max_downloads: maxDownloads,
            created_by: authStore.userId
          })
          .select()
          .single()

        if (linkError) throw linkError

        const url = `${window.location.origin}/share/${token}`
        return { success: true, data: linkData as FileShareLink, url }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create share link'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * Get all share links for a file.
   */
  const getShareLinks = async (fileId: string): Promise<FileShareLink[]> => {
    const authStore = useAuthStore()

    if (authStore.isDemoMode) {
      return []
    }

    const { supabase } = await import('@/services/supabase')
    const { data, error: fetchError } = await supabase
      .from('file_share_links')
      .select('*')
      .eq('file_id', fileId)
      .order('created_at', { ascending: false })

    if (fetchError) throw fetchError
    return (data || []) as FileShareLink[]
  }

  /**
   * Revoke a share link and delete the unencrypted copy from shared-files.
   */
  const revokeShareLink = async (linkId: string, shareBucketPath: string) => {
    const authStore = useAuthStore()
    loading.value = true

    try {
      if (authStore.isDemoMode) {
        return { success: true }
      }

      const { supabase } = await import('@/services/supabase')

      // Delete unencrypted copy from shared-files bucket
      const { error: storageError } = await supabase.storage
        .from('shared-files')
        .remove([shareBucketPath])

      if (storageError) console.warn('[FileStore] Failed to delete shared copy:', storageError.message)

      // Delete the share link record (cascade won't help here since we need to clean storage)
      const { error: deleteError } = await supabase
        .from('file_share_links')
        .delete()
        .eq('id', linkId)

      if (deleteError) throw deleteError

      return { success: true }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to revoke share link'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  // Fetch slicer profile files attached to a specific filament
  const fetchByFilament = async (filamentId: string): Promise<UserFile[]> => {
    const authStore = useAuthStore()
    if (!authStore.userId) return []

    if (authStore.isDemoMode) {
      return files.value.filter(f => f.filament_id === filamentId && f.file_type === 'profile')
    }

    try {
      const { supabase } = await import('@/services/supabase')
      const { data, error: fetchError } = await supabase
        .from('user_files')
        .select('*')
        .eq('user_id', authStore.userId)
        .eq('filament_id', filamentId)
        .eq('file_type', 'profile')
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError
      return (data || []) as UserFile[]
    } catch (err) {
      console.error('Failed to fetch filament profiles:', err)
      return []
    }
  }

  return {
    files, createdFolders, loading, error, currentFolder, _loaded, _contextKey,
    folders, filesByFolder, totalStorageUsed,
    fetchFiles, uploadFile, deleteFile, createFolder, deleteFolder, getFilesInFolder, renameFile, updateNotes, updateTags, moveFiles, duplicateFile, getDownloadUrl, getFileContent,
    uploadNewVersion, getVersions, downloadVersion, deleteVersion, restoreVersion,
    createShareLink, getShareLinks, revokeShareLink,
    fetchByFilament,
    clearData
  }
})
